import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Filters,
  ChoiceList,
  Banner,
  Pagination,
  Tabs,
  Divider,
} from '@shopify/polaris';
import { PlusIcon, DeleteIcon } from '@shopify/polaris-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

interface Order {
  id: string;
  shopify_order_id: string;
  order_number: string;
  customer_name: string;
  delivery_date: string | null;
  status: 'new' | 'pending' | 'allocated' | 'picking' | 'dispatched' | 'delivered' | 'cancelled' | 'backorder';
  total_amount: string;
  item_count: number;
  created_at: string;
  is_manual?: number;
}

interface ProductVariant {
  id: number;
  product_id: number;
  sku: string;
  title: string;
  price: string;
  product_title: string;
}

interface ManualOrderItem {
  variant_id: number;
  sku: string;
  title: string;
  quantity: number;
  price: string;
}

export default function Orders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi, error } = useApi();
  const { currentShop } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Initialize tab based on URL params
  const urlDeliveryDate = searchParams.get('delivery_date');
  const urlNoDate = searchParams.get('no_date');
  const urlOverdue = searchParams.get('overdue');

  // If delivery_date is today, select "Today's Deliveries" tab
  // Use local date (not UTC) so the label/filter matches the user's wall clock
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const initialTab = urlDeliveryDate === today ? 1 : 0;
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Filters - initialize from URL params
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState(urlDeliveryDate || '');

  // Manual order state
  const [allowManualOrders, setAllowManualOrders] = useState(false);
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createOrderError, setCreateOrderError] = useState<string | null>(null);
  const [manualOrderForm, setManualOrderForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_date: '',
    notes: '',
  });
  const [manualOrderItems, setManualOrderItems] = useState<ManualOrderItem[]>([]);

  // Product search for manual order
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductVariant[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);

  const tabs = [
    { id: 'all', content: 'All Orders' },
    { id: 'today', content: "Today's Deliveries" },
    { id: 'pending', content: 'Pending Allocation' },
    { id: 'backorder', content: 'Backorder' },
    { id: 'picking', content: 'In Picking' },
  ];

  useEffect(() => {
    loadOrders();
  }, [page, selectedTab, statusFilter, dateFilter, urlNoDate, urlOverdue]);

  // Load manual orders setting when shop is ready
  useEffect(() => {
    if (currentShop) {
      loadManualOrdersSetting();
    }
  }, [currentShop]);

  async function loadManualOrdersSetting() {
    const data = await fetchApi<{ success: boolean; allow_manual_orders: boolean }>('/settings/manual-orders');
    if (data?.success) {
      setAllowManualOrders(data.allow_manual_orders);
    }
  }

  // Search products for manual order
  const handleProductSearch = useCallback(async () => {
    if (!productSearchQuery.trim()) {
      setProductSearchResults([]);
      return;
    }

    setSearchingProducts(true);
    const data = await fetchApi<{ success: boolean; variants: ProductVariant[] }>(
      `/products/search-variants?q=${encodeURIComponent(productSearchQuery.trim())}`
    );
    setSearchingProducts(false);

    if (data?.success) {
      setProductSearchResults(data.variants || []);
    }
  }, [fetchApi, productSearchQuery]);

  // Add item to manual order
  const handleAddItemToOrder = useCallback((variant: ProductVariant) => {
    // Check if already in the list
    const existing = manualOrderItems.find(item => item.variant_id === variant.id);
    if (existing) {
      // Increment quantity
      setManualOrderItems(items =>
        items.map(item =>
          item.variant_id === variant.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      // Add new item
      setManualOrderItems(items => [
        ...items,
        {
          variant_id: variant.id,
          sku: variant.sku,
          title: variant.product_title + (variant.title !== 'Default Title' ? ` - ${variant.title}` : ''),
          quantity: 1,
          price: variant.price,
        },
      ]);
    }

    // Clear search
    setProductSearchQuery('');
    setProductSearchResults([]);
  }, [manualOrderItems]);

  // Update item quantity
  const handleUpdateItemQuantity = useCallback((variantId: number, quantity: number) => {
    if (quantity <= 0) {
      setManualOrderItems(items => items.filter(item => item.variant_id !== variantId));
    } else {
      setManualOrderItems(items =>
        items.map(item =>
          item.variant_id === variantId ? { ...item, quantity } : item
        )
      );
    }
  }, []);

  // Remove item from order
  const handleRemoveItem = useCallback((variantId: number) => {
    setManualOrderItems(items => items.filter(item => item.variant_id !== variantId));
  }, []);

  // Create manual order
  const handleCreateManualOrder = useCallback(async () => {
    if (!manualOrderForm.customer_name.trim()) {
      setCreateOrderError('Customer name is required');
      return;
    }
    if (manualOrderItems.length === 0) {
      setCreateOrderError('At least one item is required');
      return;
    }

    setCreatingOrder(true);
    setCreateOrderError(null);

    const result = await fetchApi<{ success: boolean; order?: { id: string }; error?: string }>(
      '/orders/manual',
      {
        method: 'POST',
        body: JSON.stringify({
          customer_name: manualOrderForm.customer_name.trim(),
          customer_email: manualOrderForm.customer_email.trim() || null,
          customer_phone: manualOrderForm.customer_phone.trim() || null,
          delivery_date: manualOrderForm.delivery_date || null,
          notes: manualOrderForm.notes.trim() || null,
          items: manualOrderItems.map(item => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
            price: item.price,
          })),
        }),
      }
    );

    setCreatingOrder(false);

    if (result?.success && result.order) {
      // Reset form and close modal
      setShowManualOrderModal(false);
      setManualOrderForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        delivery_date: '',
        notes: '',
      });
      setManualOrderItems([]);
      // Refresh orders list
      loadOrders();
      // Navigate to the new order
      navigate(`/orders/${result.order.id}`);
    } else {
      setCreateOrderError(result?.error || 'Failed to create order');
    }
  }, [fetchApi, manualOrderForm, manualOrderItems, navigate]);

  // Open manual order modal
  const openManualOrderModal = useCallback(() => {
    setShowManualOrderModal(true);
    setCreateOrderError(null);
    setProductSearchQuery('');
    setProductSearchResults([]);
  }, []);

  async function loadOrders() {
    const params = new URLSearchParams({
      page: String(page),
      limit: '25',
    });

    // Apply tab-based filters
    if (selectedTab === 1) {
      const t = new Date();
      params.set('delivery_date', `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
    } else if (selectedTab === 2) {
      params.set('status', 'pending');
    } else if (selectedTab === 3) {
      params.set('status', 'backorder');
    } else if (selectedTab === 4) {
      params.set('status', 'picking');
    }

    // Apply manual filters
    if (statusFilter.length > 0 && selectedTab === 0) {
      params.set('status', statusFilter.join(','));
    }
    if (dateFilter && selectedTab === 0) {
      // Only apply date filter on "All Orders" tab if not already set by tab
      params.set('delivery_date', dateFilter);
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }

    // Handle URL params from shipping calendar
    if (urlNoDate === 'true') {
      params.set('no_date', 'true');
    }
    if (urlOverdue === 'true') {
      params.set('overdue', 'true');
    }

    const data = await fetchApi<{ orders: Order[]; pagination: { total: number } }>(`/orders?${params.toString()}`);
    if (data) {
      setOrders(data.orders || []);
      setTotalCount(data.pagination?.total || 0);
    }
  }

  const handleSearch = () => {
    setPage(1);
    loadOrders();
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setSearchQuery('');
    setDateFilter('');
    setPage(1);
  };

  const statusBadge = (status: Order['status']) => {
    const config: Record<Order['status'], { tone: 'success' | 'warning' | 'info' | 'critical' | 'attention'; label: string }> = {
      new: { tone: 'info', label: 'New' },
      pending: { tone: 'warning', label: 'Pending' },
      allocated: { tone: 'info', label: 'Allocated' },
      picking: { tone: 'attention', label: 'Picking' },
      dispatched: { tone: 'info', label: 'Dispatched' },
      delivered: { tone: 'success', label: 'Delivered' },
      cancelled: { tone: 'critical', label: 'Cancelled' },
      backorder: { tone: 'critical', label: 'Backorder' },
    };
    const { tone, label } = config[status] || { tone: 'info' as const, label: status };
    return <Badge tone={tone}>{label}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Pending', value: 'pending' },
            { label: 'Allocated', value: 'allocated' },
            { label: 'Picking', value: 'picking' },
            { label: 'Dispatched', value: 'dispatched' },
            { label: 'Delivered', value: 'delivered' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = statusFilter.length > 0
    ? [{ key: 'status', label: `Status: ${statusFilter.join(', ')}`, onRemove: () => setStatusFilter([]) }]
    : [];

  // Calculate order total
  const calculateOrderTotal = () => {
    return manualOrderItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price) * item.quantity);
    }, 0).toFixed(2);
  };

  return (
    <Page
      title="Orders"
      subtitle="Pick lists tell warehouse staff which items to collect for orders. Open Pick Lists to choose orders that have stock allocated, then create a list."
      primaryAction={{
        content: 'Open Pick Lists',
        onAction: () => navigate('/pick-lists'),
      }}
      secondaryActions={allowManualOrders ? [
        {
          icon: PlusIcon, content: 'Create Manual Order',
          onAction: openManualOrderModal,
        },
      ] : []}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">Error loading orders: {error}</Banner>
          </Layout.Section>
        )}

        {/* Show banner when filtering from shipping calendar */}
        {(urlDeliveryDate || urlNoDate === 'true' || urlOverdue === 'true') && (
          <Layout.Section>
            <Banner
              tone="info"
              onDismiss={() => navigate('/orders')}
            >
              {urlDeliveryDate && (
                <Text as="span">
                  Showing orders with delivery date: <strong>{new Date(urlDeliveryDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                </Text>
              )}
              {urlNoDate === 'true' && (
                <Text as="span">Showing orders <strong>without a delivery date</strong></Text>
              )}
              {urlOverdue === 'true' && (
                <Text as="span">Showing <strong>overdue orders</strong> (past delivery date)</Text>
              )}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs
              tabs={tabs}
              selected={selectedTab}
              onSelect={(index) => {
                setSelectedTab(index);
                // Clear URL params when changing tabs
                if (urlDeliveryDate || urlNoDate || urlOverdue) {
                  navigate('/orders');
                }
              }}
            />
          </Card>
        </Layout.Section>

        {selectedTab === 1 && (
          <Layout.Section>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="span" tone="subdued">
                Showing deliveries scheduled for today ({new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })})
              </Text>
              <Button variant="plain" onClick={() => navigate('/calendar')}>
                View Shipping Calendar
              </Button>
            </InlineStack>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400" blockAlign="end">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Search"
                    labelHidden
                    placeholder="Search by order number or customer..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                    autoComplete="off"
                    connectedRight={
                      <Button onClick={handleSearch}>Search</Button>
                    }
                  />
                </div>
                <TextField
                  label="Delivery Date"
                  labelHidden
                  type="date"
                  value={dateFilter}
                  onChange={(value) => {
                    setDateFilter(value);
                    setPage(1);
                  }}
                  autoComplete="off"
                />
              </InlineStack>

              <Filters
                queryValue=""
                filters={filters}
                appliedFilters={appliedFilters}
                onQueryChange={() => {}}
                onQueryClear={() => {}}
                onClearAll={clearFilters}
              />

              {orders.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                    headings={['Order', 'Customer', 'Delivery', 'Status', 'Items', 'Total', 'Actions']}
                    rows={orders.map((order) => [
                      `#${order.order_number}`,
                      order.customer_name || '-',
                      formatDate(order.delivery_date),
                      statusBadge(order.status),
                      (order.item_count ?? 0).toString(),
                      order.total_amount ? `€${order.total_amount}` : '-',
                      <Button
                        size="slim"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        View
                      </Button>,
                    ])}
                  />

                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 1}
                      hasNext={page * 25 < totalCount}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                    <Text as="span" tone="subdued">
                      Showing {(page - 1) * 25 + 1}-{Math.min(page * 25, totalCount)} of {totalCount}
                    </Text>
                  </InlineStack>
                </>
              ) : (
                <Text as="p" tone="subdued" alignment="center">
                  No orders found matching your criteria.
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Manual Order Modal */}
      <Modal
        open={showManualOrderModal}
        onClose={() => setShowManualOrderModal(false)}
        title="Create Manual Order"
        primaryAction={{
          icon: PlusIcon, content: 'Create Order',
          onAction: handleCreateManualOrder,
          loading: creatingOrder,
          disabled: !manualOrderForm.customer_name.trim() || manualOrderItems.length === 0,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowManualOrderModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {createOrderError && (
              <Banner tone="critical" onDismiss={() => setCreateOrderError(null)}>
                {createOrderError}
              </Banner>
            )}

            <Text as="h3" variant="headingSm">Customer Details</Text>

            <TextField
              label="Customer Name"
              requiredIndicator value={manualOrderForm.customer_name}
              onChange={(value) => setManualOrderForm(prev => ({ ...prev, customer_name: value }))}
              placeholder="John Smith"
              autoComplete="name"

            />

            <InlineStack gap="200">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={manualOrderForm.customer_email}
                  onChange={(value) => setManualOrderForm(prev => ({ ...prev, customer_email: value }))}
                  placeholder="customer@example.com"
                  autoComplete="email"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Phone"
                  type="tel"
                  value={manualOrderForm.customer_phone}
                  onChange={(value) => setManualOrderForm(prev => ({ ...prev, customer_phone: value }))}
                  placeholder="+356 1234 5678"
                  autoComplete="tel"
                />
              </div>
            </InlineStack>

            <TextField
              label="Delivery Date"
              type="date"
              value={manualOrderForm.delivery_date}
              onChange={(value) => setManualOrderForm(prev => ({ ...prev, delivery_date: value }))}
              autoComplete="off"
            />

            <TextField
              label="Notes"
              value={manualOrderForm.notes}
              onChange={(value) => setManualOrderForm(prev => ({ ...prev, notes: value }))}
              placeholder="Any special instructions..."
              multiline={3}
              autoComplete="off"
            />

            <Divider />

            <Text as="h3" variant="headingSm">Order Items</Text>

            {/* Product Search */}
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search Products"
                  value={productSearchQuery}
                  onChange={setProductSearchQuery}
                  placeholder="Search by SKU or product name..."
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={handleProductSearch} loading={searchingProducts}>
                      Search
                    </Button>
                  }
                />
              </div>
            </InlineStack>

            {/* Search Results */}
            {productSearchResults.length > 0 && (
              <Card>
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Click a product to add it to the order
                  </Text>
                  {productSearchResults.slice(0, 10).map((variant) => (
                    <div
                      key={variant.id}
                      style={{ cursor: 'pointer', padding: '8px', borderRadius: '4px', backgroundColor: '#f6f6f7' }}
                      onClick={() => handleAddItemToOrder(variant)}
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="span" fontWeight="semibold">
                            {variant.product_title}
                            {variant.title !== 'Default Title' && ` - ${variant.title}`}
                          </Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            SKU: {variant.sku || 'N/A'}
                          </Text>
                        </BlockStack>
                        <Text as="span">€{variant.price}</Text>
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              </Card>
            )}

            {/* Selected Items */}
            {manualOrderItems.length > 0 && (
              <Card>
                <BlockStack gap="300">
                  {manualOrderItems.map((item) => (
                    <InlineStack key={item.variant_id} align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <Text as="span" fontWeight="semibold">{item.title}</Text>
                        <Text as="span" tone="subdued" variant="bodySm">
                          SKU: {item.sku} • €{item.price} each
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200" blockAlign="center">
                        <TextField
                          label="Quantity"
                          labelHidden
                          type="number"
                          requiredIndicator value={String(item.quantity)}
                          onChange={(value) => handleUpdateItemQuantity(item.variant_id, parseInt(value) || 0)}
                          min={1}
                          autoComplete="off"
                        />
                        <Text as="span" fontWeight="semibold">
                          €{(parseFloat(item.price) * item.quantity).toFixed(2)}
                        </Text>
                        <Button
                          variant="plain"
                          tone="critical"
                          onClick={() => handleRemoveItem(item.variant_id)}
                          icon={DeleteIcon}
                          accessibilityLabel="Remove item"
                        />
                      </InlineStack>
                    </InlineStack>
                  ))}

                  <Divider />

                  <InlineStack align="end">
                    <Text as="span" variant="headingMd" fontWeight="bold">
                      Total: €{calculateOrderTotal()}
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}

            {manualOrderItems.length === 0 && (
              <Banner tone="info">
                Search for products above to add items to the order.
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
